/**
 * Works out which kinds of stitching a shared design actually uses, from the
 * per-layer settings the creator published.
 *
 * The recorded stitch type alone isn't the answer, because rails and type
 * interact: a layer with rails renders as satin unless it explicitly asks for
 * something else, and a layer marked satin with no rails falls back to a fill
 * because satin has no path to travel. This mirrors getEffectiveLayerPasses in
 * StitchEditor/stitchRender so the labels match what actually gets drawn.
 *
 * Reports which kinds are present, not how many layers use each. Layers the
 * creator never touched carry no entry here and fall back to a global default,
 * so any count would silently omit them.
 */

const LABELS = {
  satin: "Satin",
  running: "Running",
  auto_fill: "Fill",
  legacy_fill: "Fill",
  contour_fill: "Contour fill",
  circular_fill: "Circular fill",
  meander_fill: "Meander fill",
};

// Satin first, then the fills, then running — reads as heaviest to lightest,
// which is roughly the order someone skims for.
const ORDER = ["satin", "auto_fill", "legacy_fill", "contour_fill", "circular_fill", "meander_fill", "running"];

const hasRails = (entry) => {
  if (!entry) return false;
  const pairs = entry.pairs || [];
  return pairs.some((p) => p.rail0 && p.rail1) || !!(entry.rail0 && entry.rail1);
};

/**
 * @param {{layerStitchSettings?: object, layerRails?: object}} state
 * @returns {string[]} display labels, deduped and ordered. Empty when the design
 *   was published without any per-layer tuning.
 */
export const describeStitchTypes = (state) => {
  const settings = state?.layerStitchSettings || {};
  const rails = state?.layerRails || {};
  const indices = new Set([...Object.keys(settings), ...Object.keys(rails)]);

  const present = new Set();
  for (const index of indices) {
    const perLayer = settings[index];
    const passes = perLayer?.passes || [];
    const active = passes[perLayer?.activePassIndex || 0] || passes[0];
    const explicitType = active?.stitchType;
    const railed = hasRails(rails[index]);

    // Rails win unless the layer asked for something other than satin.
    if (railed && !(explicitType && explicitType !== "satin")) {
      present.add("satin");
      continue;
    }

    for (const pass of passes) {
      const type = pass.stitchType || pass.fill_method;
      if (!type) continue;
      // Satin without rails can't be stitched as satin; the canvas draws a fill.
      present.add(type === "satin" && !railed ? "legacy_fill" : type);
    }
  }

  const seen = new Set();
  return ORDER.filter((key) => present.has(key))
    .map((key) => LABELS[key])
    .filter((label) => {
      // auto_fill and legacy_fill both read as "Fill" — collapse the duplicate.
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    });
};
